import * as load from './util/asset-loader';
import cycle from './cmpt/index';
import * as store from './util/store';
import * as translations from './util/translations';
import * as domScanner from './util/dom-scanner';
import './util/idm';
import './util/passport';
import { setupFeatureFlagsCC, setupPassportFeatureFlagsCC } from './util/feature-flags-cc';
import { checkViewOthersPermission } from './util/view-permissions';
import * as env from './util/env';
import { setupPlatformNotifications } from './util/notification-helpers';
import { setupTasks } from './util/task-helpers';
import { getSidekickConfig, getSidekickConfigCached, injectData, normalizeCacheTTL } from './util/config-helpers';
import { CONFIG_ERRORS, FL_USER_SOURCE, CACHE_CONFIG, PENDO_CONFIG, TASK_DEFAULT_POLL_TIME, TASK_DEFAULT_DOMAIN } from './util/constants';
import * as events from './util/event-bus';
import { setupSearch } from './util/search-helpers';
import startPendo, { initializePendoFromConfig } from './util/pendo-helpers';

/**
 * Initializes the Sidekick
 * @module Sidekick.Init
 * @memberOf Sidekick
 */

/**
 * Initializes the sidekick
 * @param {Object} cfg - Configuration Object
 * @param {String} cfg.token - Access Token
 * @param {String} cfg.product - Application
 * @param {Number} cfg.flId - Frontline User ID
 * @param {Element} [cfg.appElement=document.body] - Element the sidekick should be attached to
 * @param {Boolean} [cfg.addIcons=true] - Add in the icons
 * @param {Boolean} [cfg.addOpenSans=true] - Add in OpenSans
 * @param {Boolean} [cfg.addSidekickCss=true] - Add Sidekick CSS file to header
 * @param {Boolean} [cfg.scopeSidekickIcons=true] - Scope Sidekick icons to Sidekick to use specific FontAwesome version
 * @param {Boolean} [cfg.autoAppendAppNameInTitle=false] - Scans the TITLE node to ensure the application name is appended
 * @param {Boolean} [cfg.language=en] - Language to set any translations strings
 * @param {String} [cfg.customLogo] - Custom logo URL
 * @param {String} [cfg.customLogoHomeUrl] - Custom logo home URL
 * @param {Boolean} [cfg.hideAppSwitcher=false] - Hide the app switcher
 * @param {Boolean} [cfg.hideOrgSwitcher=false] - Hide the org switcher
 * @param {Boolean} [cfg.hideSearch=false] - Hide the search
 * @param {Boolean} [cfg.cleanCache=false] - Cleans out the local and session storage cache
 * @param {Boolean} [cfg.responsiveTopNav=false] - Adds resposiveness to the sidekick
 * @param {Boolean} [cfg.hideRoles=false] - Hide the roles
 * @param {Boolean} [cfg.isB2C=false] - Shows the B2C version of Sidekick
 * @param {Boolean} [cfg.hideSideNav=false] - Hides the side nav
 * @param {Object} [cfg.notificationConfig] - Configuration for the notification interface
 * @param {Boolean} [cfg.notificationConfig.usePlatformNotifications=false] - Use platform notifications
 * @param {String} [cfg.notificationConfig.applicationId=''] - Application ID for the notification interface
 * @param {Number} [cfg.notificationConfig.pollingIntervalInSeconds=60000] - Polling interval for the notification interface
 * @param {String} [cfg.notificationConfig.notificationBaseUrl=''] - Base URL for notifications
 * @param {Object} [cfg.config] - Configuration for the sidekick
 * @param {String} [cfg.config.endpoint=''] - Endpoint for the sidekick configuration
 * @param {Number} [cfg.config.retryCount=3] - Number of retries for the sidekick configuration
 * @param {Function} [cfg.config.getToken] - Function to get the token for the sidekick configuration
 * @param {Boolean} [cfg.config.useCache=true] - Whether to use intelligent configuration caching
 * @param {String} [cfg.config.storageType='local'] - Cache storage type: 'local' (default) or 'session'
 * @param {Number} [cfg.config.cacheTTL=1800000] - Cache time-to-live: values < 1000 are treated as minutes, >= 1000 as milliseconds (default: 30 minutes = 1800000ms)
 * @param {Boolean} [cfg.config.includeCredentials=false] - Whether to include credentials (cookies) in fetch requests
 * @param {Boolean} [cfg.notificationConfig.includeCredentials=false] - Whether to include credentials (cookies) in notification API requests
 * @param {String|null} [cfg.flUserSource=null] - FLuser context source controlling WHEN remote config loads (dynamic config only): null (immediate) | 'idm' | 'passport'
 * @param {Function} [cfg.flUser] - FLuser context data supplier function
 * @param {Object} [cfg.pendo] - Pendo integration configuration
 * @param {String} [cfg.pendo.apiKey] - Pendo API key for automatic initialization (requires platform_pendo_enabled feature flag)
 * @param {Number} [cfg.pendo.idleTimeout=500] - Timeout in milliseconds for requestIdleCallback fallback
 * @param {Object} [cfg.pendo.events] - Pendo lifecycle event callbacks (e.g. ready, load, guidesLoaded)
 * @param {Object} [cfg.pendo.location] - Location transforms configuration for URL manipulation
 * @param {String} [cfg.moduleName] - Module name for Pendo platform-level targeting and analytics segmentation
 * @param {String} [cfg.productId] - Product identifier for Pendo platform-level tracking and analytics segmentation
 * @param {Boolean} [cfg.enableRecruitTheme=false] - Activates the full Recruit & Hire visual design preset
 * @param {Boolean} [cfg.enableHomeroomTheme=false] - Activates the Homeroom visual design preset (independent of the Recruit theme)
 * @param {Boolean} [cfg.enableTasks=false] - Enables the Tasks interface (defaults to true when enableHomeroomTheme is on and this is not set)
 * @param {Object} [cfg.taskConfig] - Configuration for the Tasks API
 * @param {String} [cfg.taskConfig.taskBaseUrl=''] - Base URL of the Tasks API; overrides the environment default
 * @param {String} [cfg.taskConfig.taskDomain='Shared Service Test Org'] - Domain the "View all tasks" link opens the Tasks page in (the !domain query param)
 * @param {Number} [cfg.taskConfig.pollingInterval=60000] - Milliseconds between task refreshes (minimum 30000)
 */
export default function (cfg = {}) {
	// prevent a second init of the sidekick
	if (store.get('sk-config') != null) { return; }

	// Hookup feature flags
	store.onValueOnce('idm-data', (idmData) => {
		setupFeatureFlagsCC(idmData);
		// If the consumer provided a remote config endpoint for the configurable sidekick,
		// skip the legacy permission check (Morpheus Lite/passport flows don't need it).
		store.onValueOnce('sk-config', (config) => {
			const hasEndpoint = !!(config && config.config && config.config.endpoint);
			if (!hasEndpoint) {
				checkViewOthersPermission(idmData);
			}
		});
	});

	// Hookup feature flags
	store.onValueOnce('passport-data', (context) => {
		setupPassportFeatureFlagsCC(context);
		store.onValueOnce('sk-config', (config) => {
			setupPlatformNotifications();
			if (config.enableTasks) {
				store.set('tasks', []);
				setupTasks();
			}
		});
	});

	store.onValueOnce('search', (searchConfig) => {
		const { searchEndpoint, limit } = searchConfig || {};
		if (searchEndpoint) {
			setupSearch( searchEndpoint, { limit } );
		}
	});


	const bodyComponents = [
		{ name: 'header', ctxStore: false },
		{ name: 'unsupported-browser-alert', ctxStore: 'idm' },
		{ name: 'nav', ctxStore: 'nav' },
		{ name: 'pick-org-app', ctxStore: 'pick-org-app-data' },
		{ name: 'session-timeout', ctxStore: 'session-timeout' },
		{ name: 'site-errors', ctxStore: false },
		{ name: 'footer', ctxStore: false },
		{ name: 'audioeye', ctxStore: 'idm' },
	];
	const configDefaults = {
		appElement: document.body,				// the html element the sk should surround
		mainElement: null,						// the main content element for the "skip nav" link
		addIcons: true,							// automatically add the icon font
		addOpenSans: true,						// automatically add open sans font-face
		addSidekickCss: true,					// automatically add sidekick.css to the header
		scopeSidekickIcons: false,				// scope the sidekick icons to the sidekick
		autoAppendAppNameInTitle: false,		// scans the title to ensure app name is appended
		cleanCache: false,                  	// cleans out the local and session storage cache
		language: 'en',							// language to set any translations strings
		customLogo: null,				    	// custom logo url
		customLogoHomeUrl: null,	        	// custom logo home url
		hideAppSwitcher: false,					// hide the app switcher
		hideOrgSwitcher: false,					// hide the org switcher
		hideSearch: false,						// hide the search
		responsiveTopNav: false,           	 	// adds responsiveness to the sidekick
		hideRoles: false,				    	// hide the roles
		isB2C: false,							// Shows the B2C version of Sidekick
		hideSideNav: false,						// Hides the side nav
		notificationConfig: {	            	// configuration for the notification interface
			usePlatformNotifications: false, 	// use platform notifications
			applicationId: '',     				// application id for the notification interface
			pollingIntervalInSeconds: 60000,	// polling interval for the notification interface
			notificationBaseUrl: '', 			// base URL for notifications
			includeCredentials: false, 		    // whether to include credentials (cookies) in notification API requests
		},
		config: { 								// configuration for the sidekick
			endpoint: '',                       // endpoint for the sidekick configuration
			retryCount: 3, 		   				// number of retries for the sidekick configuration
			getToken: () => {}, 				// function to get the token for the sidekick configuration
			useCache: true, 					// whether to use intelligent configuration caching
			storageType: CACHE_CONFIG.DEFAULT_STORAGE, 	// cache storage type: 'local' (default) or 'session'
			cacheTTL: CACHE_CONFIG.DEFAULT_TTL, // cache time-to-live in milliseconds (default: 30 minutes)
			includeCredentials: false,			// whether to include credentials (cookies) in fetch requests
		},
		pendo: {								// Pendo analytics integration
			apiKey: null,						// Pendo API key for automatic initialization
			idleTimeout: PENDO_CONFIG.IDLE_TIMEOUT,	// requestIdleCallback timeout in milliseconds (default: 500ms)
			location: null						// Location transforms configuration (optional)
		},
		moduleName: null, 						// (internal) module name for pendo platform-level targeting
		productId: null,						// (internal) product identifier for pendo platform-level tracking
		flUserSource: null,       				// FLuser context source controlling WHEN remote config loads (dynamic config only): null (immediate) | 'idm' | 'passport'
		flUser: () => {},						// FLuser context data
		enableRecruitTheme: false,			// Activates the full Recruit & Hire visual design preset
		enableHomeroomTheme: false,			// Activates the Homeroom visual design preset (independent of the Recruit theme)
		enableTasks: false,				// Enables the Tasks interface
		taskConfig: {						// configuration for the Tasks API
			taskBaseUrl: '',					// base URL for the Tasks API (falls back to the environment default)
			taskDomain: TASK_DEFAULT_DOMAIN,	// domain the Tasks page is opened in
			pollingInterval: TASK_DEFAULT_POLL_TIME,	// milliseconds between task refreshes
		},
	};


	/**
	 * Creates the final configuration by merging defaults, user config, and optional remote config
	 * @param {Object} remoteConfig - Optional remote configuration data
	 * @returns {Object} - Merged configuration object
	 */
	function createFinalConfig(remoteConfig = null) {
		const remoteInitConfig = (remoteConfig && remoteConfig.init) || {};

		// Merge notification config defaults with any provided values
		const mergedNotificationConfig = Object.assign(
			{},
			configDefaults.notificationConfig,
			cfg.notificationConfig || {},
			remoteInitConfig.notificationConfig || {}
		);

		const mergedPendoConfig = Object.assign(
			{},
			configDefaults.pendo,
			cfg.pendo || {},
			remoteInitConfig.pendo || {}
		);
		// events holds function callbacks (ready/load/guidesLoaded) that remote JSON config
		// can never carry - merge them separately so a remote-supplied pendo object can't
		// wipe out the consumer's local callbacks.
		mergedPendoConfig.events = Object.assign(
			{},
			(cfg.pendo && cfg.pendo.events) || {},
			(remoteInitConfig.pendo && remoteInitConfig.pendo.events) || {}
		);

		const finalConfig = Object.assign({}, configDefaults, cfg, remoteInitConfig);
		finalConfig.notificationConfig = mergedNotificationConfig;
		finalConfig.taskConfig = Object.assign(
			{},
			configDefaults.taskConfig,
			cfg.taskConfig || {},
			remoteInitConfig.taskConfig || {}
		);
		// the Homeroom top bar includes the Tasks icon unless the host explicitly says otherwise
		if (finalConfig.enableHomeroomTheme && cfg.enableTasks === undefined && remoteInitConfig.enableTasks === undefined) {
			finalConfig.enableTasks = true;
		}
		finalConfig.pendo = mergedPendoConfig;
		// Normalized internal theme flags (recruit and homeroom are independent)
		finalConfig._themeEnabled = !!finalConfig.enableRecruitTheme;
		finalConfig._homeroomThemeEnabled = !!finalConfig.enableHomeroomTheme;

		return finalConfig;
	}

	/**
	 * Initializes the sidekick with the final configuration
	 * @param {Object} finalConfig - The final configuration object
	 */
	function initializeSidekick(finalConfig) {
		// setup the app config values
		store.set('sk-config', finalConfig);
		events.emit('config.loaded');
		// Initialize Pendo if API key is provided in config
		if (finalConfig.pendo && finalConfig.pendo.apiKey) {
			initializePendoFromConfig(finalConfig, startPendo);
		}
		store.set('fl-internal-product-ids', ['platformtools', 'doc']);

		translations.init(finalConfig.language);

		if (finalConfig.autoAppendAppNameInTitle) { domScanner.title(); }
		load.printStyles();

		if (finalConfig.addIcons) { load.fontFace('Fl-Icons'); }
		if (finalConfig.addOpenSans) { load.fontFace('Open-Sans'); }
		// the Homeroom theme styles are driven by the design tokens, load them first
		const startInit = () => {
			if (finalConfig._homeroomThemeEnabled) {
				load.designTokens(() => goInit(finalConfig));
			} else {
				goInit(finalConfig);
			}
		};
		if (finalConfig.addSidekickCss) {
			load.stylesheet(`${env.SIDEKICK_VERSION}/sidekick.css`, startInit);
		} else if (finalConfig.scopeSidekickIcons) {
			load.stylesheet(`${env.SIDEKICK_VERSION}/scoped-sidekick.css`, startInit);
		} else {
			startInit();
		}
	}

	/**
	 * Handles the configuration loading process
	 * @param {Object} appConfig - The full configuration object
	 */
	async function handleConfigurationLoading(appConfig) {
		let configData = null;

		// If the endpoint is provided, fetch the sidekick configuration
		const { config } = appConfig || {};
		const { endpoint, retryCount, useCache, storageType, cacheTTL } = config || {};
		if (typeof endpoint === 'string' && endpoint.trim().length) {
			// Resolve defaults defensively
			const resolvedRetryCount = (typeof retryCount === 'number' && retryCount >= 0) ? retryCount : configDefaults.config.retryCount;
			const resolvedUseCache = (typeof useCache === 'boolean') ? useCache : configDefaults.config.useCache;
			const resolvedStorageType = storageType || configDefaults.config.storageType;
			const resolvedTTL = normalizeCacheTTL(cacheTTL);

			try {
				// Choose strategy; only pass caching params to cached variant
				if (resolvedUseCache) {
					configData = await getSidekickConfigCached(
						endpoint.trim(),
						resolvedRetryCount,
						true,
						resolvedStorageType,
						resolvedTTL
					);
				} else {
					configData = await getSidekickConfig(
						endpoint.trim(),
						resolvedRetryCount
					);
				}
				store.set('sk-config-data', configData);
				// Do not inject data if we are in a UI module
				// For UI modules, data is injected by AngularJs-Framework
				const isSupersuitUiModule = window.buildConfig && window.buildConfig.isSupersuitUiModule;
				if (!isSupersuitUiModule && configData) {
					injectData(configData);
				}
			} catch (error) {
				console.error(CONFIG_ERRORS.REMOTE_CONFIG_LOAD_FAILED, error);
				events.emit('config.load-failed', { endpoint, error: error.message });
				configData = null;
			}
		}

		// If no endpoint is provided, configData remains null (uses defaults)
		const finalConfig = createFinalConfig(configData);
		initializeSidekick(finalConfig);
	}


	// Main initialization logic
	const hasEndpoint = !!(cfg.config && cfg.config.endpoint);
	const rawFlUserSource = cfg.flUserSource ? cfg.flUserSource : configDefaults.flUserSource;
	const flUserSource = typeof rawFlUserSource === 'string' ? rawFlUserSource.toLowerCase() : rawFlUserSource;

	store.set('api-user-context', {
		flUserSource,
		flUser: cfg.flUser,
		getToken: cfg.config ? cfg.config.getToken : null,
		includeCredentials: (cfg.config && typeof cfg.config.includeCredentials === 'boolean')
			? cfg.config.includeCredentials
			: configDefaults.config.includeCredentials
	});

	// Configuration loading strategy based on endpoint availability
	if (!hasEndpoint) {
		// No remote config endpoint - immediate initialization with defaults
		handleConfigurationLoading(cfg);
	} else{
		waitForUserContext();
	}

	// Has endpoint - wait for user context before loading remote config
	function waitForUserContext() {
		// When flUserSource is set, wait for that context
		if (flUserSource === FL_USER_SOURCE.PASSPORT) {
			store.onValueOnce('passport-data', (passportData) => {
				if (passportData) {handleConfigurationLoading(cfg);}
			});
		} else if (flUserSource === FL_USER_SOURCE.IDM) {
			store.onValueOnce('idm-data', (idmData) => {
				if (idmData) {handleConfigurationLoading(cfg);}
			});
		} else {
			let configLoaded = false;
			function tryLoadConfig() {
				if (!configLoaded) {
					configLoaded = true;
					handleConfigurationLoading(cfg);
				}
			};

			// Try both IDM and Passport when FLuser source is not available
			// When flUserSource is not set, wait for whichever context comes first
			store.onValueOnce('passport-data', (data) => data && tryLoadConfig());
			store.onValueOnce('idm-data', (data) => {
				const passportData = store.get('passport');
				const isMorpheusLite = passportData && passportData.isMorpheusLite;
				!isMorpheusLite && data && tryLoadConfig();
			});
		}
	};


	function goInit(finalConfig) {
		const { config } = finalConfig || {};
		const { endpoint } = config || {};
		const appEl = finalConfig.appElement;
		if(finalConfig.hideSideNav) {
			appEl.className += ' sk-nav-hidden';
		}
		if (appEl.className.length) { appEl.className += ' '; }
		appEl.className += 'sidekick-wrapped-application';
		if (finalConfig.responsiveTopNav && !finalConfig.isB2C) {
			appEl.className += ' sidekick-wrapped-application-mobile';
		}
		if (finalConfig._themeEnabled) {
			document.body.classList.add('sk--recruit-theme');
		}
		if (finalConfig._homeroomThemeEnabled) {
			document.body.classList.add('sk--homeroom-theme');
			// no responsive Homeroom design yet: responsive hosts keep the default look at small widths
			// toggle (not just add) so re-initializing with responsiveTopNav off clears a stale marker
			document.body.classList.toggle('sk--homeroom-responsive', !!(finalConfig.responsiveTopNav && !finalConfig.isB2C));
		}

		for (let i = bodyComponents.length; i--;) {
			const { name, ctxStore } = bodyComponents[i];
			const el = document.createElement('div');
			el.setAttribute(`sk-${name}`, '');
			if (ctxStore) { el.setAttribute('context-store', ctxStore); }
			if (appEl.firstChild == null) {
				appEl.appendChild(el);
			} else {
				appEl.insertBefore(el, appEl.firstChild);
			}
		}
		cycle().then(() => {
			if (endpoint) {
				// Emit an event indicating the sidekick has fully loaded for the given endpoint
				events.emit('sidekick.loaded');
			}
		});
	}
}
